<script lang="ts">
const __DsBlock = {
	type: 'input',
	icon: 'lucide:text-cursor-input',
	name: '$t:blocks.input_textarea.name',
	description: '$t:blocks.input_textarea.description',
	category: 'input',
	tags: ['form-control'],
	fieldBinding: { supportedTypes: ['text'] },
	options: [
		{
			name: 'modelValue',
			model: true,
		},
		{
			name: 'placeholder',
		},
		{
			name: 'autoresize',
		},
		{
			name: 'color',
			type: 'enum',
			values: BLOCK_COLOR_OPTIONS,
		},
		{
			name: 'variant',
			type: 'enum',
			values: BLOCK_INPUT_VARIANT_OPTIONS,
		},
		{
			name: 'size',
			type: 'enum',
			values: BLOCK_CONTROL_SIZE_OPTIONS,
		},
		{
			name: 'leadingIcon',
			block: BuiltinBlockIds.InputIcon,
		},
		{
			name: 'trailingIcon',
			block: BuiltinBlockIds.InputIcon,
		},
		{
			name: 'loading',
		},
		{
			name: 'disabled',
		},
		{
			name: 'readonly',
		},
		{
			name: 'disabledNotice',
		},
		{
			name: 'modelModifiers',
			hidden: true,
		},
	],
}
</script>
<script lang="ts" setup>
import type { TextareaProps } from '#layers/ui';
import type { InputBlockProps } from '~/types/input-block';
import { reactivePick } from '@vueuse/core';
import { BuiltinBlockIds } from 'virtual:monospace-builtin-block-ids';
import { computed } from 'vue';

import { defineBlock } from '#layers/block-engine';
import { BLOCK_COLOR_OPTIONS, BLOCK_CONTROL_SIZE_OPTIONS, BLOCK_INPUT_VARIANT_OPTIONS } from '#layers/blocks/constants/ui-options';
import { useDisabledNotice } from '#layers/ui';
import { usePrimitiveFormField } from '~/composables/form-block/use-primitive-form-field';

interface Props extends InputBlockProps, Omit<TextareaProps, 'readonly'> {
	autoresize?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	autoresize: true,
});
const model = defineModel<string>();

;

const forwarded = reactivePick(props, 'placeholder', 'color', 'variant', 'size', 'leadingIcon', 'trailingIcon', 'loading', 'avatar', 'class', 'readonly');
const { modelValue, disabled, disabledNotice } = usePrimitiveFormField(props, { model });
const { showDisabledNotice } = useDisabledNotice({ disabled, disabledNotice });

const rows = computed(() => {
	if (props.rows !== undefined) return props.rows;
	return props.autoresize ? 5 : undefined;
});
</script>

<template>
	<DTooltip :delay-duration="0" :disabled="!showDisabledNotice" :text="disabledNotice">
		<DTextarea
			v-bind="forwarded"
			v-model="modelValue"
			:model-modifiers="modelModifiers ?? {}"
			:disabled
			class="min-w-full"
			:autoresize
			:rows
			:aria-readonly="readonly"
			:ui="{
				base: 'max-h-40 min-h-10 resize-y overflow-y-auto',
			}"
		/>
	</DTooltip>
</template>
